import { useAtomValue } from 'jotai'
import { isNil } from 'ramda'

import type {
  ArticleButtonComponent,
  ArticleIconButtonComponent,
  LinksButtonComponent,
  LinksLinkComponent,
  LinksSimpleLinkComponent,
  Navigation,
  NavigationLinkwithiconComponent,
} from '@twisto/api-cms'

import {
  Button as ArticleButton,
  BaseLink,
  IconButton,
} from 'homepage/components/atoms'

import { navigationThemeAtom } from '../../navigation-theme'
import { secondaryButton } from '../secondary-navigation-slot.styles'

import { Button, Link, LinkWithIcon } from './'

type Props = {
  secondaryNavigationContent: Navigation['secondary']
  hideOnMobile?: boolean
  increaseFontSize?: boolean
  source?: 'main' | 'modal'
}

type LinkComponent =
  | (LinksLinkComponent & { __component: 'links.link' })
  | (LinksButtonComponent & { __component: 'links.button' })
  | (LinksSimpleLinkComponent & { __component: 'links.simple-link' })
  | (ArticleButtonComponent & {
      __component: 'article.button'
    })
  | (ArticleIconButtonComponent & {
      __component: 'article.icon-button'
    })
  | (NavigationLinkwithiconComponent & {
      __component: 'navigation.linkwithicon'
    })

export const SecondaryComponents = ({
  secondaryNavigationContent,
  hideOnMobile = false,
  increaseFontSize,
  source = 'main',
}: Props) => {
  const navigationTheme = useAtomValue(navigationThemeAtom)

  if (isNil(secondaryNavigationContent)) {
    return null
  }

  const componentsArray = secondaryNavigationContent.map((component, index) => {
    const linkComponent = component as LinkComponent

    switch (linkComponent.__component) {
      case 'navigation.linkwithicon':
        return (
          <LinkWithIcon
            key={index}
            hideOnMobile={hideOnMobile}
            increaseFontSize={increaseFontSize}
            link={linkComponent}
          />
        )
      case 'links.link':
        return (
          <Link
            key={index}
            hideOnMobile={hideOnMobile}
            increaseFontSize={increaseFontSize}
            link={linkComponent as LinksLinkComponent}
          />
        )
      case 'links.simple-link':
        return (
          <BaseLink
            key={index}
            fontWeight="medium"
            hideOnMobile={hideOnMobile}
            hover="primary"
            inverted={navigationTheme === 'dark'}
            variant="primary"
          />
        )
      case 'links.button':
        return (
          <Button
            key={index}
            increaseFontSize
            link={linkComponent}
            variant={
              navigationTheme === 'dark' && source === 'main'
                ? 'inverted'
                : 'default'
            }
          />
        )
      case 'article.button':
        return (
          <ArticleButton
            key={index}
            styles={secondaryButton}
            {...linkComponent}
          />
        )
      case 'article.icon-button':
        return (
          <IconButton
            key={index}
            hideOnMobile={hideOnMobile}
            styles={secondaryButton}
            {...linkComponent}
          />
        )
      default:
        return null
    }
  })

  return <>{componentsArray}</>
}
